import { bowlDecreaseMl, mealWeights } from './intake.js';
import { eventObservationDetail } from './events.js';
import { treatmentPlanAt } from './treatment-lifecycle.js';
const STOOL_FORM = { firm: '단단함', usual: '보통', soft: '무름', unformed: '형태 없음', watery: '물 같음' };
const STOOL_COLOR = { usual: '평소 색', black: '검은색', reddish: '붉은 기', yellow: '노란색', other: '다른 색' };
const CLUMP_SIZE = { smaller: '평소보다 작음', usual: '평소 크기', bigger: '평소보다 큼' };
const VITAL_LABEL = { weight: ['체중', 'kg'], sleepingRespiratoryRate: ['수면 호흡수', '회/분'], temperature: ['체온', '℃'], heartRate: ['심박', '회/분'], bloodGlucose: ['혈당', ' mg/dL'] };
const STATUS = { given: '했어요', skipped: '건너뛰었어요', refused: '거부했어요', partial: '일부만 했어요' };
const SCALE = { good: '좋았어요', usual: '평소와 같아요', notGood: '평소와 달라요' };
/** A single factual projection for the daily review and the complete history. */
export function buildRecordHistory(petId, rows) {
    const output = [];
    const date = (value) => Date.parse(`${value}T00:00:00+09:00`);
    const add = (table, row, at, title, detail, family) => {
        if (row.petId !== petId || row.deletedAt || row.duplicateOf || !Number.isFinite(at))
            return;
        output.push({ actorLabel: row.actorLabel, id: `${table}:${row.id}`, recordId: row.id, revision: row.updatedAt, table, at, title, detail, family });
    };
    for (const r of rows.events)
        add('events', r, r.occurredAt, '직접 본 사실', eventObservationDetail(r), 'observation');
    for (const r of rows.vitals) {
        const [label, unit] = VITAL_LABEL[r.type] ?? ['측정값', ''];
        const notes = [...new Set([r.method === 'heldScale' ? '안고 잼' : '', r.note?.trim()].filter(Boolean))];
        add('vitals', r, r.measuredAt, label, [`${r.value}${unit}`, ...notes].join(' · '), 'observation');
    }
    for (const r of rows.treatmentLogs) {
        const treatment = rows.treatments.find(t => t.id === r.treatmentId && t.petId === petId);
        const plan = treatment ? treatmentPlanAt(treatment, r.recordedAt) : null;
        add('treatmentLogs', r, r.recordedAt, plan?.name || '투약·처치', `${STATUS[r.status]}${r.actorLabel ? ` · ${r.actorLabel} 기록` : ''}${r.note ? ` · ${r.note}` : ''}`, 'care');
    }
    for (const r of rows.meals) {
        const weights = mealWeights(r);
        add('meals', r, r.servedAt, r.foodName || '식사·물', `준 양 ${weights.servedG}g · ${weights.leftoverG == null ? '남긴 양 미입력' : `남긴 양 ${weights.leftoverG}g`} · ${r.waterMl == null ? '물 미관찰' : `물 ${r.waterMl}ml`}`, 'care');
    }
    for (const r of rows.coughLogs) {
        const observed = (kind) => r[`${kind}Observed`] ?? r[`${kind}Count`] > 0;
        add('coughLogs', r, date(r.date), '기침 관찰', `낮 ${observed('day') ? `${r.dayCount}회` : '미관찰'} · 밤 ${observed('night') ? `${r.nightCount}회` : '미관찰'}`, 'observation');
    }
    for (const r of rows.gaitLogs)
        add('gaitLogs', r, r.startedAt, '산책·보행', `${Math.round(r.durationSec / 60)}분${r.distanceM == null ? '' : ` · ${r.distanceM}m`}${r.stoppedEarly ? ' · 중간에 마침' : ''}`, 'observation');
    for (const r of rows.checkins) {
        const axes = [['appetite', '식욕'], ['energy', '활력'], ['discomfort', '불편함'], ['elimination', '배변'], ['sleep', '수면']];
        add('checkins', r, date(r.date), '컨디션', axes.map(([key, label]) => `${label} ${r[key] ? SCALE[r[key]] : '미관찰'}`).join(' · '), 'observation');
    }
    for (const r of rows.qolRecords) {
        const values = [r.hurt, r.hunger, r.hydration, r.hygiene, r.happiness, r.mobility, r.moreGoodDays].filter((v) => v != null);
        add('qolRecords', r, date(r.date), '삶의 질 관찰', `${values.length}/7항목 기록 · 입력한 점수 ${values.reduce((a, b) => a + b, 0)}${values.length < 7 ? ' · 나머지 미관찰' : ''}`, 'observation');
    }
    for (const r of rows.visits)
        add('visits', r, r.visitedAt, r.clinicName || '병원 방문', [r.reason, r.note, r.nextVisitDate ? `다음 방문 ${r.nextVisitDate}` : null].filter(Boolean).join(' · ') || '방문일 기록', 'hospital');
    for (const r of rows.labs)
        add('labs', r, r.testedAt, `검사 ${r.code}`, `${r.value} ${r.unit}${r.clinicName ? ` · ${r.clinicName}` : ''}`, 'hospital');
    for (const r of rows.costs)
        add('costs', r, r.occurredAt, '지출', `${r.amountKrw.toLocaleString('ko-KR')}원${r.note ? ` · ${r.note}` : ''}`, 'care');
    for (const r of rows.routineLogs)
        add('routineLogs', r, r.scheduledAt, rows.routines.find(t => t.id === r.routineId)?.name || '반복 돌봄', r.status === 'done' ? '했어요' : '건너뛰었어요', 'care');
    for (const r of rows.carePlans)
        add('carePlans', r, date(r.sourceCheckedOn), '병원에서 들은 계획', `${r.title} · ${r.sourceLabel}`, 'hospital');
    for (const r of rows.careQuestions)
        add('careQuestions', r, date(r.answeredOn || r.askedOn), r.answeredOn ? '진료 뒤 답' : '물어볼 질문', `${r.text}${r.answer ? ` · ${r.answer}` : ''}`, 'hospital');
    for (const r of rows.careEpisodes)
        add('careEpisodes', r, r.startsAt, '맡김·미용', `${r.title} · ${r.status === 'returned' ? '돌아온 뒤 확인' : r.status === 'active' ? '진행 중' : '준비 중'}`, 'care');
    for (const r of rows.eliminations ?? []) {
        const parts = r.kind === 'stool'
            ? [r.count != null ? `${r.count}회` : null, r.stoolForm ? STOOL_FORM[r.stoolForm] : null, r.stoolColor ? STOOL_COLOR[r.stoolColor] : null]
            : [r.clumpCount != null ? `감자 ${r.clumpCount}개` : r.count != null ? `${r.count}회` : null, r.clumpSize ? CLUMP_SIZE[r.clumpSize] : null];
        add('eliminations', r, r.occurredAt, r.kind === 'stool' ? '배변' : '소변', [...parts, r.attachmentIds?.length ? `사진 ${r.attachmentIds.length}` : null, r.note].filter(Boolean).join(' · ') || '본 사실 기록', 'observation');
    }
    for (const r of rows.waterLogs ?? []) {
        const decrease = bowlDecreaseMl(r);
        add('waterLogs', r, r.filledAt, '물그릇', `채운 양 ${r.filledMl}ml · ${decrease == null ? '남은 양 미입력' : `그릇에서 줄어든 양 ${decrease}ml`}${r.wetFoodG == null ? '' : ` · 함께 입력한 먹은 습식 ${r.wetFoodG}g`}`, 'care');
    }
    for (const r of rows.photoDays ?? [])
        add('photoDays', r, date(r.date) + 12 * 3_600_000, '오늘 사진', r.caption || '사진 1장', 'observation');
    for (const r of rows.dayMarks ?? [])
        if (r.kind === 'quiet')
            add('dayMarks', r, date(r.date) + 12 * 3_600_000, '별일 없었어요', '보호자가 확인한 날', 'observation');
    return output.sort((a, b) => b.at - a.at || a.id.localeCompare(b.id));
}
